import * as React from 'react';

import { DetailGrid, DetailRow } from './DetailList/DetailList.js';
import { DetailSection } from './DetailList/DetailSection.js';
import type { DimeCardShellMetaItem } from './DimeCardShellMeta.js';
import { cn } from './lib/utils.js';

/** A DimeCardShell meta item whose icon is optional, so a dense card can list bare label/value rows. */
export type DimeDetailRow = Omit<DimeCardShellMetaItem, 'icon'> & {
  icon?: DimeCardShellMetaItem['icon'];
  /** `inline` keeps this row's label beside its value in a stacked list, for a short fact such as a note's Updated. */
  layout?: 'inline';
  /** A secondary line under the row spanning the card's full width, such as a figure's basis, so it needn't wrap in the value column. */
  detail?: React.ReactNode;
};

export type DimeDetailRowGroup = Readonly<{ title?: string; rows: DimeDetailRow[] }>;

type DimeDetailRowsProps = Readonly<{
  groups: DimeDetailRowGroup[];
  /** `stacked` puts each label above its value, for a narrow rail where inline values wrap mid-word. */
  layout?: 'inline' | 'stacked';
  className?: string;
}>;

/** The detail cards' label/value rows: an icon and quiet label on the left, the value on the right (or under it when stacked). */
export function DimeDetailRows({ groups, layout = 'inline', className }: DimeDetailRowsProps) {
  return (
    <DetailGrid
      justified
      align="start"
      sectioned
      className={cn(
        'text-[13px] leading-4 [&_dd]:min-w-0 [&_dd]:break-words [&_dd]:font-normal [&_dd]:text-foreground [&_dt]:font-normal [&_dt]:text-muted-foreground',
        layout === 'stacked'
          ? 'grid-cols-1 gap-y-0 [&>dl>dd]:mt-0.5 [&>dl>dd]:mb-3 [&>dl:last-child>dd:last-child]:mb-0 [&_p]:col-span-1'
          : 'gap-x-3 [&_dd]:text-right',
        className,
      )}
    >
      {groups.map((group, index) => (
        <DetailSection key={group.title ?? `group-${index}`} title={group.title}>
          {group.rows.map((row, rowIndex) => {
            const key = `${row.label}-${rowIndex}`;
            const detailRow = (
              <DetailRow
                key={key}
                icon={row.icon}
                label={row.label}
                value={row.value}
                placeholder="Not Set"
              />
            );
            const placedRow =
              layout === 'stacked' && row.layout === 'inline' ? (
                <div
                  key={key}
                  className="mb-3 flex items-start justify-between gap-3 last:mb-0 [&>dd]:text-right"
                >
                  {detailRow}
                </div>
              ) : (
                detailRow
              );
            if (row.detail == null) return placedRow;
            return (
              <React.Fragment key={key}>
                {placedRow}
                <dd data-slot="detail-row-detail" className="col-span-full -mt-1">
                  <span className="text-xs text-muted-foreground">{row.detail}</span>
                </dd>
              </React.Fragment>
            );
          })}
        </DetailSection>
      ))}
    </DetailGrid>
  );
}
